import { Suspense, useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { MobileNav } from "./MobileNav";
import { Topbar } from "./Topbar";
import { QuickAddChooser } from "@/components/transactions/QuickAddChooser";
import { TransactionFormModal } from "@/components/transactions/TransactionFormModal";
import { VoiceQuickAdd } from "@/components/transactions/VoiceQuickAdd";
import { DailyLogReminder } from "@/components/transactions/DailyLogReminder";
import { InterstitialVideoAd } from "@/components/ui/InterstitialVideoAd";
import { FloatingMascot } from "@/components/mascot/FloatingMascot";
import { SubscribeReminder } from "@/components/billing/SubscribeReminder";
import { DemoBanner } from "./DemoBanner";
import { useAuth } from "@/context/AuthContext";
import { onShowInterstitialAd } from "@/lib/interstitialAdTrigger";
import { setOnWriteBlocked } from "@/lib/apiClient";
import { trackPixelEventOncePerVisit } from "@/lib/metaPixel";
import { useQuickAddGuard } from "@/lib/paywall";
import styles from "./AppLayout.module.css";

export function AppLayout() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isGuest = !user;
  const guardQuickAdd = useQuickAddGuard();
  const [chooserOpen, setChooserOpen] = useState(false);
  const [manualFormOpen, setManualFormOpen] = useState(false);
  const [voiceQuickAddOpen, setVoiceQuickAddOpen] = useState(false);
  const [showInterstitial, setShowInterstitial] = useState(false);

  useEffect(() => onShowInterstitialAd(() => setShowInterstitial(true)), []);

  // Rede de segurança pra gravação barrada sem um `guard` explícito no clique: visitante sem
  // conta vai pro cadastro, conta sem assinatura vai pra tela de pagamento.
  useEffect(() => {
    setOnWriteBlocked(() => navigate(isGuest ? "/cadastro" : "/premium"));
    return () => setOnWriteBlocked(null);
  }, [navigate, isGuest]);

  // Meta Pixel: o visitante que abre a demonstração viu o produto (era o papel da página de
  // venda, que deixou de ser a porta de entrada).
  useEffect(() => {
    if (isGuest) trackPixelEventOncePerVisit("ViewContent");
  }, [isGuest]);

  const openQuickAdd = () => guardQuickAdd(() => setChooserOpen(true));

  return (
    <div className={styles.shell}>
      <Sidebar onQuickAdd={openQuickAdd} />
      <div className={styles.content}>
        <Topbar />
        <main className={styles.main}>
          {isGuest && <DemoBanner />}
          {/* Limite próprio de carregamento: sem ele, baixar o pedaço de uma página trocaria o app
              inteiro (menu e faixa da demonstração junto) pelo carregador de tela cheia. */}
          <Suspense fallback={<div className={styles.pageLoading}><span className="loading-spin" aria-hidden /></div>}>
            <Outlet />
          </Suspense>
        </main>
      </div>
      <MobileNav onQuickAdd={openQuickAdd} />

      <QuickAddChooser
        isOpen={chooserOpen}
        onClose={() => setChooserOpen(false)}
        onSelectVoice={() => {
          setChooserOpen(false);
          setVoiceQuickAddOpen(true);
        }}
        onSelectManual={() => {
          setChooserOpen(false);
          setManualFormOpen(true);
        }}
      />
      <TransactionFormModal isOpen={manualFormOpen} onClose={() => setManualFormOpen(false)} />
      <VoiceQuickAdd isOpen={voiceQuickAddOpen} onClose={() => setVoiceQuickAddOpen(false)} />
      {showInterstitial && <InterstitialVideoAd onClose={() => setShowInterstitial(false)} />}
      <FloatingMascot />
      <SubscribeReminder />
      <DailyLogReminder onQuickAdd={openQuickAdd} />
    </div>
  );
}
